/**
 * ShareSheet — Spec 128: Unified Sharing UX
 */

import React, { useState, useMemo } from 'react';
import { useOrganization } from '../../contexts/OrganizationContext';
import { useOrganizationMembers } from '../../hooks/useOrganizationMembers';
import { useFeatures } from '../../contexts/FeaturesContext';
import { isDomRole, isSubRole } from '../../utils/roleHelpers';
import { MemberChecklistGrid } from './MemberChecklistGrid';
import { getErrorMessage } from '../../utils/errorUtils';
import { Button } from '../ui/Button';
import { Modal } from '../ui/Modal';
import { FeatureUpgradePrompt } from '../FeatureUpgradePrompt';
import { useExistingSharesQuery, useCreateShareMutation, useRemoveShareMutation } from '../../hooks/queries/useSharing';

export type ShareEntityType = 'journal' | 'scene' | 'media' | 'album' | 'inventory' | 'scenePlan';
export type SharePermission = 'view' | 'edit';

// Spec 249 — sharing paywall. Maps a ShareSheet entityType to its tierGate key (computed
// server-side by getTierGateStatus in featureFlagService.ts). 'album' deliberately rides
// 'shareMedia' — same flag covers media items and albums (see isShareMediaEnabled's comment).
// 'inventory' and 'scenePlan' are intentionally absent — not in scope per the CEO's decision
// (docs/design/sharing-paywall-upsell-375px.html § Scope) — so they're never gated here.
const TIER_GATE_KEY_BY_ENTITY_TYPE: Partial<Record<ShareEntityType, string>> = {
  journal: 'shareJournal',
  scene: 'shareScene',
  media: 'shareMedia',
  album: 'shareMedia',
};

// Role-forked copy for the "Add people" paywall (docs/design/sharing-paywall-upsell-375px.html
// Frame A/B). Destination is identical for both roles (FeatureUpgradePrompt's own /pricing
// default) — only the copy and whether a purchase-implying CTA is shown differs.
const SHARE_PAYWALL_COPY = {
  dom: {
    featureName: 'Add people',
    benefit: 'This hierarchy is on the Free plan. Upgrade to Pro to share with more people.',
  },
  sub: {
    featureName: 'Add people',
    benefit: 'This hierarchy is on the Free plan. Ask your Dominant to upgrade to Pro to share scenes, journals, and media.',
  },
} as const;

export interface PendingShare {
  userId: number;
  userName: string;
  userEmail?: string;
  permission: SharePermission;
}

interface ShareSheetProps {
  entityType: ShareEntityType;
  entityId?: string | number;
  entityTitle: string;
  mode: 'modal' | 'inline' | 'panel';
  isOpen?: boolean;
  onClose?: () => void;
  pendingShares?: PendingShare[];
  onPendingSharesChange?: (shares: PendingShare[]) => void;
  onShareSuccess?: () => void;
  showPermissions?: boolean;
  defaultExpanded?: boolean;
}

const ENTITY_LABELS: Record<ShareEntityType, string> = {
  journal: 'journal entry', scene: 'scene', media: 'media item', album: 'album', inventory: 'inventory item', scenePlan: 'scene plan',
};

const CASCADE_HINTS: Partial<Record<ShareEntityType, string>> = {
  journal: 'Sharing this entry will also share any linked scenes and media.',
  album: 'Sharing this album will also share all media items in it.',
};

export const ShareSheet: React.FC<ShareSheetProps> = ({
  entityType, entityId, entityTitle, mode, isOpen, onClose,
  pendingShares = [], onPendingSharesChange, onShareSuccess,
  showPermissions, defaultExpanded = false,
}) => {
  const { organization } = useOrganization();
  const { tierGate } = useFeatures();
  const isCreateMode = entityId === undefined || entityId === null;
  const shouldShowPermissions = showPermissions ?? (entityType === 'scene' || entityType === 'scenePlan');

  // Spec 249 — sharing paywall. Set only when the org does NOT meet the required tier for
  // this entity type (empty/absent tierGate = qualifying org, nothing to gate). Applies
  // regardless of isCreateMode — pending shares added here still hit the gated create
  // endpoint once the parent form submits, so blocking them here too avoids a confusing
  // "added to the list, then 402'd on save" flow.
  const gateKey = TIER_GATE_KEY_BY_ENTITY_TYPE[entityType];
  const requiredTier = gateKey ? tierGate[gateKey] : undefined;
  const isDom = isDomRole(organization?.role);
  const isSub = isSubRole(organization?.role);
  const paywallCopy = isSub ? SHARE_PAYWALL_COPY.sub : SHARE_PAYWALL_COPY.dom;

  const [expanded, setExpanded] = useState(defaultExpanded);
  const [selectedMemberIds, setSelectedMemberIds] = useState<number[]>([]);
  const [selectedPermission, setSelectedPermission] = useState<SharePermission>('view');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const isActive = mode === 'modal' ? !!isOpen : expanded;
  const entityIdStr = entityId !== undefined && entityId !== null ? String(entityId) : undefined;

  const { data: existingShares, isLoading: loadingShares } = useExistingSharesQuery(
    entityType, entityIdStr, isActive && !isCreateMode,
  );
  const createShareMutation = useCreateShareMutation();
  const removeShareMutation = useRemoveShareMutation();
  // Only ever true mid-flight on a REAL network share (create mode never calls
  // the mutation — see handleShare's early return below), matching the
  // pre-migration `sharing` flag's exact scope.
  const sharing = createShareMutation.isPending;
  const removingUserId = removeShareMutation.isPending ? removeShareMutation.variables?.userId : null;

  const existingShareUserIds = useMemo(() => existingShares.map(s => s.sharedWithUserId), [existingShares]);
  const excludeUserIds = isCreateMode ? pendingShares.map(s => s.userId) : existingShareUserIds;

  const { members: rawMembers, loading: loadingMembers } = useOrganizationMembers({
    organizationId: isActive ? organization?.id : undefined,
    excludeUserIds, excludeSelf: true,
  });

  const availableMembers = useMemo(() => rawMembers.map(m => ({ id: m.id, label: m.label })), [rawMembers]);
  const shareCount = isCreateMode ? pendingShares.length : existingShares.length;

  const handleShare = async () => {
    if (selectedMemberIds.length === 0) { setError('Please select at least one member to share with'); return; }
    if (isCreateMode) {
      const newShares: PendingShare[] = selectedMemberIds.map(userId => {
        const member = rawMembers.find(m => m.id === userId);
        return { userId, userName: member?.label || 'Unknown', userEmail: member?.subLabel, permission: selectedPermission };
      });
      onPendingSharesChange?.([...pendingShares, ...newShares]);
      setSelectedMemberIds([]);
      setSuccess(`Added ${newShares.length} member(s) to share list`);
      setTimeout(() => setSuccess(''), 3000);
      return;
    }
    setError(''); setSuccess('');
    try {
      const orgId = organization?.id ? parseInt(organization.id, 10) : 0;
      await createShareMutation.mutateAsync({
        entityType, entityId: entityIdStr as string, memberIds: selectedMemberIds, permission: selectedPermission, orgId,
      });
      setSuccess(`Shared with ${selectedMemberIds.length} member(s)`);
      setSelectedMemberIds([]);
      onShareSuccess?.();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) { setError(getErrorMessage(err)); }
  };

  const handleRemoveShare = async (_shareId: number, userId: number) => {
    if (isCreateMode) { onPendingSharesChange?.(pendingShares.filter(s => s.userId !== userId)); return; }
    setError('');
    try {
      await removeShareMutation.mutateAsync({ entityType, entityId: entityIdStr as string, userId });
      onShareSuccess?.();
    } catch (err) { setError(getErrorMessage(err)); }
  };

  const handleClose = () => {
    if (!sharing) { setSelectedMemberIds([]); setSelectedPermission('view'); setError(''); setSuccess(''); onClose?.(); }
  };

  // Spec 249 — the paywall's dismiss must NOT use FeatureUpgradePrompt's page-gate default
  // (navigate(-1)), which would navigate away from the entry/scene/media screen underneath
  // this sheet. 'inline' mode isn't an overlay at all (an accordion embedded in the page), so
  // dismiss there just collapses it; modal/panel modes close the sheet/popover exactly like
  // the existing Close button does.
  const handleGateDismiss = () => {
    if (mode === 'inline') { setExpanded(false); return; }
    handleClose();
  };

  const displayShares = isCreateMode
    ? pendingShares.map(s => ({ id: s.userId, userId: s.userId, name: s.userName, email: s.userEmail, permission: s.permission }))
    : existingShares.map(s => ({ id: s.id, userId: s.sharedWithUserId, name: s.sharedWithUserName, email: s.sharedWithUserEmail, permission: s.permission }));

  const entityLabel = ENTITY_LABELS[entityType] || 'item';
  const cascadeHint = CASCADE_HINTS[entityType] || null;

  const renderContent = () => (
    <div className="border-t border-border-light flex flex-col gap-4 p-3 sm:p-4">
      {mode === 'inline' && cascadeHint && (
        <div className="mt-2 px-2 py-1 bg-[rgba(184,115,51,0.1)] dark:bg-[rgba(184,115,51,0.2)] rounded-sm text-xs text-accent">{cascadeHint}</div>
      )}

      {error && <div className="px-3 py-2 bg-danger/10 border border-danger/30 rounded text-[var(--theme-error)] dark:text-[var(--theme-danger-muted)] text-[13px]" role="alert">{error}</div>}
      {success && <div className="px-3 py-2 bg-success/10 border border-success rounded text-[var(--theme-success)] dark:text-[var(--theme-success-muted)] text-[13px]" role="status">{success}</div>}

      <div className="mb-1">
        <label className="block text-[13px] font-medium text-text-secondary mb-2">
          {isCreateMode ? 'Will share with:' : 'Shared with:'}
        </label>
        {loadingShares && !isCreateMode ? (
          <div className="p-3 text-center text-text-tertiary text-[13px]">Loading shares...</div>
        ) : displayShares.length === 0 ? (
          <p className="py-2 text-text-tertiary text-[13px] italic m-0">Not shared with anyone yet.</p>
        ) : (
          <div className="flex flex-col gap-2">
            {displayShares.map(share => (
              <div key={share.id} className="flex items-center gap-2.5 bg-bg-secondary dark:bg-bg-tertiary border border-border-light dark:border-border-medium rounded transition-colors hover:bg-bg-tertiary dark:hover:bg-bg-secondary px-2.5 sm:px-3 py-2 sm:py-2.5">
                <div className="flex-1 flex flex-col gap-0.5 min-w-0">
                  <span className="text-sm font-medium text-text-primary overflow-hidden text-ellipsis whitespace-nowrap">{share.name}</span>
                  {share.email && <span className="text-xs text-text-tertiary overflow-hidden text-ellipsis whitespace-nowrap">{share.email}</span>}
                </div>
                {shouldShowPermissions && (
                  <span className={`text-xs px-2 py-[3px] rounded-sm shrink-0 ${share.permission === 'edit' ? 'bg-[rgba(184,115,51,0.1)] dark:bg-[rgba(184,115,51,0.2)] text-accent' : 'bg-[rgba(107,114,128,0.1)] dark:bg-[rgba(107,114,128,0.2)] text-text-secondary'}`}>
                    {share.permission === 'edit' ? 'Edit' : 'View'}
                  </span>
                )}
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => handleRemoveShare(share.id, share.userId)}
                  disabled={removingUserId === share.userId}
                  aria-label="Remove access"
                  className="w-6 h-6 p-0 rounded-full text-base font-medium leading-none shrink-0 hover:bg-danger/10 hover:text-danger dark:hover:bg-danger/20"
                >
                  {removingUserId === share.userId ? '...' : '\u00D7'}
                </Button>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="pt-4 border-t border-border-light">
        {requiredTier ? (
          // Spec 249 — gate ONLY this "Add people" sub-section, never the existing-shares
          // list above (which always renders, on every tier, with its remove buttons intact —
          // see docs/design/sharing-paywall-upsell-375px.html § "Critical correction").
          <FeatureUpgradePrompt
            featureName={paywallCopy.featureName}
            requiredTier={requiredTier}
            benefit={paywallCopy.benefit}
            canUpgrade={isDom}
            onDismiss={handleGateDismiss}
          />
        ) : (
          <>
            <label className="block text-[13px] font-medium text-text-secondary mb-2">Add people:</label>
            {loadingMembers ? (
              <div className="p-3 text-center text-text-tertiary text-[13px]">Loading members...</div>
            ) : availableMembers.length === 0 ? (
              <p className="py-2 text-text-tertiary text-[13px] italic m-0">
                {displayShares.length > 0 ? 'All members already have access' : 'No other members in your hierarchy'}
              </p>
            ) : (
              <>
                <MemberChecklistGrid options={availableMembers} selectedIds={selectedMemberIds} onChange={setSelectedMemberIds} disabled={sharing} />

                {shouldShowPermissions && selectedMemberIds.length > 0 && (
                  <div className="mt-3">
                    <label className="block text-[13px] font-medium text-text-secondary mb-2">Permission:</label>
                    <div className="flex flex-col gap-1.5 sm:gap-2">
                      {(['view', 'edit'] as SharePermission[]).map(perm => (
                        <label
                          key={perm}
                          className={`flex items-start gap-2 px-3 py-2 border rounded cursor-pointer transition-all hover:bg-bg-secondary dark:hover:bg-bg-tertiary ${selectedPermission === perm ? 'border-accent bg-[rgba(184,115,51,0.05)] dark:bg-[rgba(184,115,51,0.15)]' : 'border-border-light dark:border-border-medium'}`}
                        >
                          <input type="radio" name="share-sheet-permission" value={perm} checked={selectedPermission === perm} onChange={() => setSelectedPermission(perm)} disabled={sharing} className="hidden" />
                          <span className="flex flex-col gap-0.5">
                            <strong className="text-sm font-medium text-text-primary">{perm === 'view' ? 'View Only' : 'Can Edit'}</strong>
                            <span className="text-xs text-text-secondary">{perm === 'view' ? 'Can view but not edit' : 'Can view and edit'}</span>
                          </span>
                        </label>
                      ))}
                    </div>
                  </div>
                )}

                {selectedMemberIds.length > 0 && (
                  <div className="flex justify-end mt-3">
                    <Button variant="primary" onClick={handleShare} disabled={sharing} className="w-full min-w-0 sm:min-w-[180px]">
                      {sharing ? 'Sharing...' : `Share with ${selectedMemberIds.length} member(s)`}
                    </Button>
                  </div>
                )}
              </>
            )}
          </>
        )}
      </div>
    </div>
  );

  // Modal Mode
  if (mode === 'modal') {
    if (!isOpen) return null;
    return (
      <Modal
        isOpen={!!isOpen}
        onClose={handleClose}
        title={`Share ${entityLabel}`}
        size="md"
        variant="centered"
        footer={
          <Button variant="secondary" onClick={handleClose} disabled={sharing} className="min-w-[100px]">Close</Button>
        }
      >
        <p className="m-0 text-sm text-text-secondary">
          Share <strong className="text-accent font-semibold">&quot;{entityTitle}&quot;</strong> with hierarchy members
        </p>
        {cascadeHint && <p className="mt-2 px-2 py-1 bg-[rgba(184,115,51,0.1)] dark:bg-[rgba(184,115,51,0.2)] rounded-sm text-xs text-accent">{cascadeHint}</p>}
        {renderContent()}
      </Modal>
    );
  }

  // Panel Mode — raw content, no accordion wrapper (used inside Sheet slide panel)
  if (mode === 'panel') {
    return <div className="py-2">{renderContent()}</div>;
  }

  // Inline Mode
  return (
    <div className="mt-4 border border-border-light rounded overflow-hidden bg-bg-primary">
      <Button
        type="button"
        variant="ghost"
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center gap-2 bg-bg-secondary text-sm font-medium text-text-primary text-left hover:bg-bg-tertiary focus:outline-2 focus:outline-accent focus:outline-offset-[-2px] px-3 sm:px-4 py-2.5 sm:py-3 rounded-none justify-start"
      >
        <span className="flex items-center text-text-secondary">
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M4 7L8 3L12 7" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M8 3V11" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
            <path d="M3 13H13" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
          </svg>
        </span>
        <span className="flex-1 flex items-center gap-2">
          Sharing
          {shareCount > 0 && (
            <span className="inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 text-xs font-semibold text-accent bg-[rgba(184,115,51,0.15)] rounded-lg">{shareCount}</span>
          )}
        </span>
        <span className="text-[10px] text-text-secondary">
          {expanded ? '\u25B2' : '\u25BC'}
        </span>
      </Button>

      {expanded && renderContent()}
    </div>
  );
};
